import { useQuery, useQueryClient, UseQueryResult, useSuspenseQuery } from '@tanstack/react-query';

import { ApiError } from '../../types/Error';
import { PaginatedScanTypes } from './schemas';
import { type ScanTypesParams, scanTypesService } from './service';

const getScanTypesQueryKey = (params?: ScanTypesParams) => ['scan-types', params];

type UseScanTypesOptions = {
    params?: ScanTypesParams;
};

export const useScanTypes = ({ params }: UseScanTypesOptions) =>
    useQuery({
        queryKey: getScanTypesQueryKey(params),
        queryFn: ({ signal }) => scanTypesService.getScanTypes({ params, signal }),
        placeholderData: (previousData) => previousData,
        retry: 1
    });

export const useAllScanTypes = ({ params }: UseScanTypesOptions): UseQueryResult<PaginatedScanTypes, ApiError> =>
    useQuery({
        queryKey: getScanTypesQueryKey(params),
        queryFn: ({ signal }) => scanTypesService.getAllScanTypesMerged({ params, signal }),
        placeholderData: (previousData: any) => previousData,
        retry: 1
    });

export const getScanTypeDetailsQueryKey = (id: number) => ['scan-type-details', id];

export const useScanTypeDetailsSuspense = (id: number) =>
    useSuspenseQuery({
        queryKey: getScanTypeDetailsQueryKey(id),
        queryFn: ({ signal }) => scanTypesService.getScanType(id, { signal }),
        retry: 1
    });

export const usePrefetchScanType = (id: number) => {
    const queryClient = useQueryClient();

    return () =>
        queryClient.prefetchQuery({
            queryKey: getScanTypeDetailsQueryKey(id),
            queryFn: ({ signal }) => scanTypesService.getScanType(id, { signal }),
            // cache the prefetched data for 30 seconds, otherwise it will be garbage collected immediately if no `useQuery` is using it
            staleTime: 30 * 1_000
        });
};
